import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { publicApi } from '../lib/api';
import { Check, FileText, Lock, X } from 'lucide-react';
import { LogoMark } from '../components/Logo';

export default function PublicSignatureVerify() {
  const { verificationId } = useParams();
  const [signature, setSignature] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    publicApi.get(`/signatures/verify/${verificationId}`)
      .then(({ data }) => setSignature(data.signature))
      .catch(() => setSignature({ status: 'not_found' }))
      .finally(() => setLoading(false));
  }, [verificationId]);

  if (loading) return <div className="min-h-screen grid place-items-center text-sm text-zinc-500">Loading...</div>;
  const valid = signature?.status === 'valid';
  const formatSignatureType = (value) => {
    const normalized = String(value || '').toLowerCase();
    if (normalized === 'ses') return 'Simple Electronic Signature';
    if (normalized === 'aes') return 'Advanced Electronic Signature';
    return value || 'Electronic Signature';
  };
  const cryptoStatus = signature?.cryptographic_valid === true
    ? { label: 'Valid', className: 'border-green-200 bg-green-50 text-green-700' }
    : signature?.cryptographic_valid === false
      ? { label: 'Invalid', className: 'border-red-200 bg-red-50 text-red-700' }
      : { label: 'Not available', className: 'border-zinc-200 bg-white text-zinc-500' };

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LogoMark size={25} className="shrink-0"/>
            <span className="font-display text-lg font-bold">idemGO</span>
            <span className="font-mono text-xs text-zinc-400 hidden sm:inline">/ signature authenticity check</span>
          </div>
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-500 font-mono"><Lock className="w-3 h-3"/>encrypted</span>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-10 sm:py-12">
        <div className="w-full max-w-xl mx-auto border border-zinc-200 bg-white rounded-sm p-6 sm:p-8">
          <span className={`mx-auto w-12 h-12 grid place-items-center border rounded-sm ${
            valid ? 'text-green-700 bg-green-50 border-green-200' : 'text-red-700 bg-red-50 border-red-200'
          }`}>
            {valid ? <Check className="w-5 h-5" strokeWidth={1.5}/> : <X className="w-5 h-5" strokeWidth={1.5}/>}
          </span>
          <h1 className="font-display text-2xl font-bold text-center mt-4">
            {valid ? 'Valid signature' : 'Signature not found'}
          </h1>
          <p className="text-sm text-zinc-600 text-center mt-2">
            {valid ? signature.statement : 'We could not verify this signature ID.'}
          </p>

          {valid && (
            <div className="mt-6 text-sm border border-zinc-200 rounded-sm p-4 bg-zinc-50">
              <div className="flex items-center gap-2 text-zinc-700 mb-3">
                <FileText className="w-4 h-4 shrink-0" strokeWidth={1.5}/>
                <span className="font-medium min-w-0 break-words">{signature.title}</span>
              </div>
              <div className="flex justify-between py-1 gap-4"><span className="text-zinc-500">Signer</span><span className="font-mono text-right">{signature.signer_display_name}</span></div>
              <div className="flex justify-between py-1 gap-4"><span className="text-zinc-500">Signed at</span><span className="font-mono text-right">{new Date(signature.signed_at).toLocaleString()}</span></div>
              <div className="flex justify-between py-1 gap-4"><span className="text-zinc-500">Signature method</span><span className="font-mono text-right">{formatSignatureType(signature.signature_level)}</span></div>
              <div className="flex justify-between py-1 gap-4"><span className="text-zinc-500">Signature reference</span><span className="font-mono text-right">{signature.signature_reference || signature.signature_verification_id || signature.verification_id}</span></div>
              <div className="flex justify-between py-1 gap-4"><span className="text-zinc-500">Verification method</span><span className="font-mono text-right">{signature.auth_method === 'kyc' ? 'KYC verification' : 'Email link'}</span></div>
              {signature.kyc_verification_id && (
                <div className="pt-3 mt-3 border-t border-zinc-200">
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Verification ID</div>
                  <div className="font-mono text-xs break-all mt-1">{signature.kyc_verification_id}</div>
                </div>
              )}
              <div className="pt-3 mt-3 border-t border-zinc-200">
                <div className="text-xs uppercase tracking-widest text-zinc-500">Document hash</div>
                <div className="font-mono text-xs break-all mt-1">{signature.document_hash}</div>
              </div>
              <div className="flex items-center justify-between gap-4 pt-3 mt-3 border-t border-zinc-200">
                <span className="text-zinc-500">Cryptographic verification</span>
                <span className={`inline-flex items-center border px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${cryptoStatus.className}`}>
                  {cryptoStatus.label}
                </span>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
